import { getUpscIstDayRangeIso } from '@cont/UpscCurrentAffairs/upscIstDate';

export const UPSC_CA_ARTICLES_CMS_API_PATH = 'upsc-ca-articles';

export const UPSC_CA_ARTICLES_RECENT_EDITIONS_LIMIT = 6;

export const UPSC_CA_ARTICLES_RECENT_EDITIONS_PARAMS = {
  _limit: UPSC_CA_ARTICLES_RECENT_EDITIONS_LIMIT,
  _sort: 'publishAt:DESC'
};

export const UPSC_CA_ARTICLES_TODAY_ANALYSIS_LIMIT = 25;
export const UPSC_CA_ARTICLES_TODAY_ANALYSIS_PARAMS = {
  _limit: UPSC_CA_ARTICLES_TODAY_ANALYSIS_LIMIT,
  _sort: 'publishAt:DESC'
};

const getPublishAtDayRange = (date) => getUpscIstDayRangeIso(date);

export const getUpscCaArticlesTodayAnalysisParamsForDate = (date) => ({
  ...UPSC_CA_ARTICLES_TODAY_ANALYSIS_PARAMS,
  ...getPublishAtDayRange(date)
});

export const UPSC_CA_ARTICLES_CALENDAR_PARAMS = {
  _sort: 'publishAt:DESC'
};

export const getUpscCaArticlesParamsForPublishAtDate = (date) => ({
  ...UPSC_CA_ARTICLES_CALENDAR_PARAMS,
  ...getPublishAtDayRange(date)
});

export const getUpscCaArticlesFetchKey = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, value]) => value != null && value !== ''
  );

  if (!entries.length) {
    return UPSC_CA_ARTICLES_CMS_API_PATH;
  }

  const search = new URLSearchParams(
    entries.map(([key, value]) => [key, String(value)])
  ).toString();

  return `${UPSC_CA_ARTICLES_CMS_API_PATH}?${search}`;
};

const TEXT_BLOCK = 'upsc-current-affairs.text-block';
const FACT_SESSION = 'upsc-current-affairs.fact-session';
const BULLET_SECTION = 'upsc-current-affairs.bullet-section';
const MCQ_SECTION = 'upsc-current-affairs.mcq-section';
const LINK_SECTION = 'upsc-current-affairs.link-section';

const STRAPI_COMPONENT_FIELD = '__component';

// camelcase-keys converts Strapi's `__component` to `component` when useCamelCase is true.
const getContentComponentType = (block) =>
  block?.component ?? block?.[STRAPI_COMPONENT_FIELD];

const getBlockLabel = (block, componentType) => {
  const heading = (block?.heading || '').trim();
  const mainHeading = (block?.mainHeading || '').trim();

  switch (componentType) {
    case TEXT_BLOCK:
    case BULLET_SECTION:
      return heading || mainHeading;
    case FACT_SESSION:
    case MCQ_SECTION:
    case LINK_SECTION:
      return mainHeading || heading;
    default:
      return heading || mainHeading;
  }
};

const normalizeTextBlock = (block) => {
  const description = (block?.description || '').trim();

  if (!description) {
    return null;
  }

  return {
    id: block?.id,
    component: 'text-block',
    label: getBlockLabel(block, TEXT_BLOCK),
    description
  };
};

const normalizeFactSession = (block) => {
  const facts = Array.isArray(block?.facts)
    ? block.facts
        .map((fact) => ({
          heading: (fact?.factHeading || '').trim(),
          value: (fact?.factPoint || '').trim()
        }))
        .filter((fact) => fact.heading || fact.value)
    : [];

  if (!facts.length) {
    return null;
  }

  return {
    id: block?.id,
    component: 'fact-session',
    label: getBlockLabel(block, FACT_SESSION),
    keyFacts: facts
  };
};

const normalizeBulletSection = (block) => {
  const points = Array.isArray(block?.points)
    ? block.points.map((item) => (item?.point || '').trim()).filter(Boolean)
    : [];

  if (!points.length) {
    return null;
  }

  return {
    id: block?.id,
    component: 'bullet-section',
    label: getBlockLabel(block, BULLET_SECTION),
    points
  };
};

const normalizeMcqSection = (block) => {
  const options = Array.isArray(block?.options)
    ? block.options
        .map((option) => (option?.option || '').trim())
        .filter(Boolean)
    : [];

  if (!block?.mcqQuestion || !options.length) {
    return null;
  }

  const correctOptionIndex = Array.isArray(block?.options)
    ? block.options.findIndex((option) => option?.isCorrect)
    : -1;

  const hintHeading = (block?.hintHeading || '').trim();
  const hintExplanation = (block?.hintExplanation || '').trim();
  const hint =
    hintHeading || hintExplanation
      ? { heading: hintHeading, description: hintExplanation }
      : undefined;

  return {
    id: block?.id,
    component: 'mcq-section',
    label: getBlockLabel(block, MCQ_SECTION),
    mcq: {
      subHeading: (block?.mcqHeading || block?.mainHeading || '').trim(),
      question: (block?.mcqQuestion || '').trim(),
      options,
      correctOptionIndex,
      ...(hint ? { hint } : {})
    },
    mainsQuestion: {
      subHeading: (block?.mainsHeading || '').trim(),
      question: (block?.mainsExplanation || block?.mainsQuestion || '').trim()
    }
  };
};

const normalizeLinkSection = (block) => {
  const links = Array.isArray(block?.items)
    ? block.items
        .map((item) => ({
          label: (item?.text || '').trim(),
          link: (item?.link || '').trim()
        }))
        .filter((item) => item.label)
    : [];

  if (!links.length) {
    return null;
  }

  return {
    id: block?.id,
    component: 'link-section',
    label: getBlockLabel(block, LINK_SECTION),
    links
  };
};

const normalizeContentBlock = (block) => {
  switch (getContentComponentType(block)) {
    case TEXT_BLOCK:
      return normalizeTextBlock(block);
    case FACT_SESSION:
      return normalizeFactSession(block);
    case BULLET_SECTION:
      return normalizeBulletSection(block);
    case MCQ_SECTION:
      return normalizeMcqSection(block);
    case LINK_SECTION:
      return normalizeLinkSection(block);
    default:
      return null;
  }
};

const getArticleTitle = (topicHeading, mainHeading) => {
  const topic = (topicHeading || '').trim();
  const heading = (mainHeading || '').trim();

  if (topic && heading) {
    return `${topic}: ${heading}`;
  }

  return topic || heading;
};

export const normalizeUpscCaArticleItem = (item) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const topicHeading = (item?.topicHeading || '').trim();
  const articleMainHeading = (item?.mainHeading || '').trim();
  const gsPaper = Array.isArray(item?.upscGsTags)
    ? item.upscGsTags.map((tag) => (tag?.heading || '').trim()).filter(Boolean)
    : (item?.gsPaper || '')
        .split(/\s+/)
        .map((tag) => tag.trim())
        .filter(Boolean);

  const contentBlocks = Array.isArray(item?.content)
    ? item.content.map(normalizeContentBlock).filter(Boolean)
    : [];

  const upscSubject = item?.upscSubject
    ? {
        id: item.upscSubject.id,
        heading: (item.upscSubject.heading || '').trim(),
        slug: (item.upscSubject.slug || '').trim(),
        icon: (item.upscSubject.icon || '').trim()
      }
    : undefined;

  const normalized = {
    id: item?.id,
    slug: (item?.slug || '').trim(),
    topicHeading,
    mainHeading: getArticleTitle(topicHeading, articleMainHeading),
    subHeading: (item?.subHeading || '').trim(),
    reference: (item?.reference || '').trim(),
    gsPaper,
    upscSubject,
    dateSlug: item?.dateSlug,
    titleSlug: item?.titleSlug,
    createdAt: item?.createdAt,
    publishAt: item?.publishAt,
    metaTitle: (item?.metaTitle || '').trim(),
    metaDescription: (item?.metaDescription || '').trim(),
    contentBlocks
  };

  return normalized.id && normalized.mainHeading ? normalized : null;
};

export const normalizeUpscCaArticleResponse = (data) =>
  normalizeUpscCaArticleItem(data);

export const normalizeUpscCaArticlesResponse = (data) => {
  const rawItems = Array.isArray(data) ? data : [];

  return rawItems.map(normalizeUpscCaArticleItem).filter(Boolean);
};

export const getUpscCaArticleCmsPath = (id) =>
  `${UPSC_CA_ARTICLES_CMS_API_PATH}/${id}`;
